import { HeadphonesOutline24Icon } from "@vygruppen/spor-icon-react";
import { HorizontalContainer } from "features/Header/PuzzelTelephone/components/Logon/StyledLogonComponents";
import { useCallStatus } from "features/Header/PuzzelTelephone/provider/contexts/CallStatusContext";
import { usePuzzelExtendedStateInformation } from "features/Header/PuzzelTelephone/provider/contexts/ListenForCallStatusContext";
import { usePuzzelAuthenticationContext } from "features/Header/PuzzelTelephone/provider/contexts/PuzzelAuthenticationContext";
import {
  usePerformUserLogOnOff,
  useUserDetailsState,
} from "features/Header/PuzzelTelephone/provider/contexts/UserDetailsAndLogOnOffContext";
import { useUserValidationState } from "features/Header/PuzzelTelephone/provider/contexts/UserValidationContext";
import {
  removePhoneNumberPrefix,
  validPhoneNumber,
} from "features/Header/PuzzelTelephone/utils";
import { FormEvent, useCallback, useEffect, useMemo } from "react";
import { SkeletonButton } from "shared/components/feedback/SkeletonLoader/SkeletonLoaderStyles";
import { InputField } from "shared/components/forms/Inputfield";
import { Select } from "shared/components/forms/Select";
import { DropsButton } from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import styled from "styled-components";

type PuzzelProfile = {
  groupProfile: boolean;
  id: string;
  name: string;
  number: number;
  phoneType: string;
  qualities: Array<unknown>;
  phoneNumber?: string;
  lastPhoneNumber?: string;
};

type SelectItem = {
  value: string;
  text: string;
};

// "Velg et alternativ" is 18 characters
const ProfileContainer = styled.div<{ $longestLabel: number | undefined }>`
  button > :first-child > :first-child {
    transform: scale(0.8) translateY(-2px) translateX(-5px);
  }
  min-width: ${({ $longestLabel }) =>
    `${Math.max($longestLabel ?? 18, 18) + 5}ch`};
`;

const Logon = () => {
  const {
    phoneNumber: nonPrefixedPhoneNumber,
    profileId,
    setPhoneNumber,
    setProfileId,
  } = useCallStatus();

  const userDetails = useUserDetailsState();
  const { userStateInformation } = usePuzzelExtendedStateInformation();
  const { phoneNumberPrefix } = usePuzzelAuthenticationContext();
  const { userId } = useUserValidationState();
  const performUserLogOnOff = usePerformUserLogOnOff();

  const { currentProfile, profiles, isLoggedOn } = userDetails;
  const enableBtn = profileId && validPhoneNumber(nonPrefixedPhoneNumber);

  const items: SelectItem[] | undefined = useMemo(
    () =>
      profiles?.map((profile: PuzzelProfile) => ({
        text: profile.name,
        value: profile.id,
      })),
    [profiles],
  );

  const longestLabel = useMemo(
    () =>
      items?.reduce(
        (prev, curr) => (curr.text.length > prev ? curr.text.length : prev),
        0,
      ),
    [items],
  );

  const logOn = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      performUserLogOnOff({
        param: {
          userId,
          logOnOrOff: "logon",
        },
        payload: {
          phoneNumber: phoneNumberPrefix + nonPrefixedPhoneNumber,
          profileId,
        },
      });
    },
    [
      performUserLogOnOff,
      userId,
      phoneNumberPrefix,
      nonPrefixedPhoneNumber,
      profileId,
    ],
  );

  const logOff = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      performUserLogOnOff({
        param: {
          userId,
          logOnOrOff: "logoff",
        },
        payload: {},
      });
    },
    [performUserLogOnOff, userId],
  );

  useEffect(() => {
    if (currentProfile?.id) {
      setProfileId(currentProfile?.id);
    }
  }, [currentProfile?.id]);

  if (isLoggedOn === null) {
    return <SkeletonButton />;
  }

  if (
    !isLoggedOn ||
    userStateInformation?.contactCentreStatus === "LoggedOff"
  ) {
    return (
      <HorizontalContainer onSubmit={logOn}>
        <ProfileContainer $longestLabel={longestLabel}>
          <Select
            name="profileId"
            label="Telefonkø"
            value={profileId}
            onChange={(e) => {
              const { value } = e.target;
              if (typeof value !== "string") return;
              setProfileId(value);
            }}
          >
            {items?.map((item) => (
              <option value={item.value} key={item.value}>
                {item.text}
              </option>
            ))}
          </Select>
        </ProfileContainer>

        <InputField
          type="text"
          onChange={(event) => {
            setPhoneNumber(event.target.value);
          }}
          value={nonPrefixedPhoneNumber}
          placeholder="Telefonnummer"
        />
        <DropsButton
          ml="12px"
          size="sm"
          variant="secondary"
          type="submit"
          disabled={!enableBtn}
          leftIcon={<HeadphonesOutline24Icon />}
        >
          Logg på
        </DropsButton>
      </HorizontalContainer>
    );
  }

  return (
    <HorizontalContainer onSubmit={logOff}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            marginRight: "1rem",
          }}
        >
          <Text>{currentProfile?.name}</Text>
          <Text>
            (
            {removePhoneNumberPrefix(
              currentProfile.phoneNumber,
              phoneNumberPrefix,
            )}
            )
          </Text>
        </div>
        <DropsButton
          size="sm"
          variant="secondary"
          type="submit"
          leftIcon={<HeadphonesOutline24Icon />}
        >
          Logg av
        </DropsButton>
      </div>
    </HorizontalContainer>
  );
};

export default Logon;
